import { useMutation, useQuery } from "@pinia/colada";
import { ref } from "vue";

import { useGeoProductCategories } from "@solvari/components";
import { handleQuery } from "@solvari/utils";

import { ziggy } from "@/shared/libs/ziggy";
import { useResolveZipcode } from "~/shared/api/geocoder.api";
import { useHostLocale } from "~/shared/composables/useHost";

type Destination = "municipality" | "product" | "productOverview";

export function useLocationSearchTarget() {
  const locale = useHostLocale();

  const zipCode = ref("");

  const { refresh: refreshLocalSearchProducts } = useQuery({
    ...useGeoProductCategories(
      { localeCode: locale, hasLocalSearch: true },
      handleQuery,
    ),
    enabled: () => !!zipCode.value,
  });

  const { mutateAsync: resolveZipcode } = useMutation(useResolveZipcode());

  async function findMunicipalityUrl(productSlug: string) {
    const { data: localSearchProducts } = await refreshLocalSearchProducts();
    if (!localSearchProducts?.some((product) => product.slug === productSlug)) {
      return null;
    }
    try {
      const municipality = await resolveZipcode({
        localeCode: locale,
        zipcode: zipCode.value,
      });
      return ziggy("mainSite.localSearch.municipality", {
        geoProductSlug: productSlug,
        municipality: municipality.municipalitySlug,
        province: municipality.provinceSlug,
      });
    } catch {
      // useResolveZipcode reports to Sentry; 404 or failure → product page.
      return null;
    }
  }

  function toTarget(url: string, destination: Destination) {
    return { url, tracking: { destination, postcodeFilled: !!zipCode.value } };
  }

  async function resolveTarget(product?: { slug: string; url: string }) {
    if (!product) {
      return toTarget(ziggy("mainSite.productOverview"), "productOverview");
    }
    if (!zipCode.value) {
      return toTarget(product.url, "product");
    }
    const municipalityUrl = await findMunicipalityUrl(product.slug);
    return municipalityUrl
      ? toTarget(municipalityUrl, "municipality")
      : toTarget(product.url, "product");
  }

  return { zipCode, resolveTarget };
}
